import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IProductivityCoverage } from '@interfaces/IProductivity';
import { ChevronDownIcon } from '@utils/icons';
import { CoverageChecklist } from './CoverageChecklist';
import { hasCoverageGap, isUnconfigured, largestShortfall } from './coverageLines';
import styles from './CoverageBar.module.css';

interface ICoverageBarProps {
  coverage: IProductivityCoverage | undefined;
  siteId?: string | undefined;
}

/**
 * The standing reminder that the figures below cover only part of the plan.
 *
 * A real project is never at 100% coverage, and the dangerous surface is not the
 * blank page — it is a confident-looking table silently built on a fraction of
 * the activities. So this stays visible for as long as any activity is
 * unmeasurable, and disappears the moment the plan is fully authored.
 */
export function CoverageBar({ coverage, siteId }: ICoverageBarProps) {
  const { t } = useTranslation();

  // `null` = follow the default; a click pins it. Not plain `useState(default)`,
  // because coverage arrives after the first render and a state initialiser
  // would have already frozen the wrong default.
  const [pinned, setPinned] = useState<boolean | null>(null);

  if (!coverage || !hasCoverageGap(coverage)) return null;

  // Nothing measurable at all → lead with the whole story; the page below has
  // nothing to say yet. A partly-measured plan opens to a single line, because
  // its figures are worth reading first.
  const expanded = pinned ?? isUnconfigured(coverage);
  const shortfall = largestShortfall(coverage);

  return (
    <div className={styles.root}>
      <button
        type="button"
        className={styles.summary}
        onClick={() => setPinned(!expanded)}
        aria-expanded={expanded}
      >
        <span className={styles.measured}>
          {t('productivity.coverage.partial', {
            measured: coverage.measured,
            total: coverage.total,
          })}
        </span>
        {shortfall && (
          <>
            <span className={styles.separator} aria-hidden="true">
              ·
            </span>
            <span className={styles.shortfall}>
              {t(`productivity.coverage.missing.${shortfall.prerequisite}`, {
                count: shortfall.count,
              })}
            </span>
          </>
        )}
        <span className={styles.caret} data-collapsed={!expanded}>
          <ChevronDownIcon size={16} />
        </span>
      </button>

      {expanded && (
        <div className={styles.detail}>
          <CoverageChecklist coverage={coverage} siteId={siteId} />
          <p className={styles.note}>{t('productivity.coverage.partialInfo')}</p>
        </div>
      )}
    </div>
  );
}